const USERNAME_PATTERN = /^[a-zA-Z0-9_]{3,20}$/;

function projectRef(url) {
  try {
    return new URL(url).hostname.split('.')[0];
  } catch {
    return '';
  }
}

function storageKey(url) {
  const ref = projectRef(url);
  return ref ? `sb-${ref}-auth-token` : '';
}

function defaultStorage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

async function readJson(response) {
  return response.json().catch(() => null);
}

export class DungeonMarketAuth {
  constructor({
    url = '',
    anonKey = '',
    fetchImpl = globalThis.fetch,
    storage,
    requestTimeoutMs = 12000,
  } = {}) {
    this.url = url.replace(/\/$/, '');
    this.anonKey = anonKey;
    this.fetchImpl = fetchImpl;
    this.storage = storage === undefined ? defaultStorage() : storage;
    this.key = storageKey(this.url);
    this.requestTimeoutMs = Math.max(10, Math.min(60000, Math.round(Number(requestTimeoutMs) || 12000)));
    this.sessionOverride = undefined;
    this.persistenceError = null;
    this.restoreError = null;
    this.restoreOffline = false;
    this.restoreRetryable = false;
    this.restorePromise = null;
    this.sessionRevision = 0;
  }

  get configured() {
    return Boolean(this.url && this.anonKey && this.fetchImpl);
  }

  get session() {
    if (this.sessionOverride !== undefined) return this.sessionOverride;
    return this.readStoredSession();
  }

  readStoredSession() {
    if (!this.key || !this.storage) return null;
    try {
      const value = JSON.parse(this.storage.getItem(this.key) || 'null');
      return value?.access_token ? value : null;
    } catch {
      return null;
    }
  }

  get signedIn() {
    return Boolean(this.session);
  }

  get username() {
    const user = this.session?.user;
    return user?.user_metadata?.username || user?.email?.split('@')[0] || null;
  }

  syncStorageEvent(event) {
    if (!this.key || event?.key !== this.key) return false;
    this.sessionRevision += 1;
    this.sessionOverride = undefined;
    this.restoreError = null;
    this.restoreOffline = false;
    this.restoreRetryable = false;
    this.persistenceError = null;
    return true;
  }

  writeSession(session) {
    this.sessionRevision += 1;
    this.sessionOverride = session || null;
    this.restoreError = null;
    this.restoreOffline = false;
    this.restoreRetryable = false;
    if (!this.key || !this.storage) {
      this.persistenceError = 'Browser session storage is unavailable.';
      return { ok: false, error: this.persistenceError };
    }
    try {
      if (session) this.storage.setItem(this.key, JSON.stringify(session));
      else this.storage.removeItem(this.key);
      this.persistenceError = null;
      return { ok: true };
    } catch (error) {
      this.persistenceError = error instanceof Error
        ? error.message
        : 'Browser session storage is unavailable.';
      return { ok: false, error: this.persistenceError };
    }
  }

  async restore() {
    if (this.restorePromise) return this.restorePromise;
    this.restorePromise = this.restoreSession();
    try {
      return await this.restorePromise;
    } finally {
      this.restorePromise = null;
    }
  }

  async restoreSession() {
    // An offline refresh intentionally masks the expired access token in this
    // tab. A later restore must still re-read the preserved refresh token.
    const session = this.restoreError && this.sessionOverride === null
      ? this.readStoredSession()
      : this.session;
    if (!session) return null;
    const restoreRevision = this.sessionRevision;
    const expiresAt = Number(session.expires_at || 0) * 1000;
    if (!expiresAt || expiresAt > Date.now() + 60000) return session;
    if (!session.refresh_token) {
      if (restoreRevision === this.sessionRevision) this.writeSession(null);
      return null;
    }
    const result = await this.request('auth/v1/token?grant_type=refresh_token', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: session.refresh_token }),
    });
    if (!result.ok || !result.data?.access_token) {
      if (restoreRevision !== this.sessionRevision) return this.session;
      if (result.retryable || result.offline) {
        // Keep the refresh token in storage for a later attempt, but do not
        // expose an expired access token to cloud requests in this tab.
        this.sessionOverride = null;
        this.restoreError = result.error;
        this.restoreOffline = Boolean(result.offline);
        this.restoreRetryable = true;
        return null;
      }
      this.writeSession(null);
      return null;
    }
    if (restoreRevision !== this.sessionRevision) return this.session;
    const refreshed = { ...result.data, user: result.data.user || session.user };
    this.writeSession(refreshed);
    this.restoreError = null;
    this.restoreOffline = false;
    this.restoreRetryable = false;
    return refreshed;
  }

  async request(path, options = {}) {
    if (!this.configured) return { ok: false, error: 'Cloud accounts are not configured.' };
    const controller = new AbortController();
    const externalSignal = options.signal;
    let timedOut = false;
    const forwardAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', forwardAbort, { once: true });
    }
    const deadline = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.requestTimeoutMs);
    try {
      const response = await this.fetchImpl.call(globalThis, `${this.url}/${path}`, {
        ...options,
        signal: controller.signal,
        headers: {
          apikey: this.anonKey,
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
      });
      const data = await readJson(response);
      if (!response.ok) {
        const retryable = response.status === 408
          || response.status === 425
          || response.status === 429
          || response.status >= 500;
        return {
          ok: false,
          offline: response.status === 408 || response.status >= 500,
          retryable,
          status: response.status,
          error: data?.msg || data?.message || data?.error_description || 'Account request failed.',
        };
      }
      return { ok: true, data };
    } catch (error) {
      return {
        ok: false,
        offline: true,
        retryable: true,
        timedOut,
        error: timedOut
          ? 'Account request timed out. Check your connection and try again.'
          : error?.message || 'Account service is unreachable.',
      };
    } finally {
      clearTimeout(deadline);
      externalSignal?.removeEventListener?.('abort', forwardAbort);
    }
  }

  async resolveEmail(identifier) {
    if (identifier.includes('@')) return { ok: true, email: identifier };
    const result = await this.request('rest/v1/rpc/email_for_username', {
      method: 'POST',
      headers: { Authorization: `Bearer ${this.anonKey}` },
      body: JSON.stringify({ lookup_username: identifier }),
    });
    if (!result.ok) {
      if (result.offline) return result;
      return { ok: false, error: 'Incorrect username or password.' };
    }
    if (typeof result.data !== 'string' || !result.data) {
      return { ok: false, error: 'Incorrect username or password.' };
    }
    return { ok: true, email: result.data };
  }

  async ensureProfile(user, username, accessToken = this.session?.access_token) {
    if (!user?.id || !username) return { ok: false, error: 'Missing profile information.' };
    if (!accessToken) return { ok: true, username };
    const existing = await this.request(`rest/v1/profiles?select=username&id=eq.${encodeURIComponent(user.id)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (existing.ok && existing.data?.[0]?.username) return { ok: true, username: existing.data[0].username };
    const inserted = await this.request('rest/v1/profiles', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}`, Prefer: 'resolution=ignore-duplicates,return=minimal' },
      body: JSON.stringify({ id: user.id, username }),
    });
    return inserted.ok ? { ok: true, username } : inserted;
  }

  async signIn(identifier, password) {
    if (typeof identifier !== 'string' || typeof password !== 'string' || !identifier.trim() || !password) {
      return { ok: false, error: 'Enter your username or email and password.' };
    }
    if (identifier.trim().length > 254 || password.length > 128) {
      return { ok: false, error: 'Account credentials exceed the supported length.' };
    }
    const requestRevision = this.sessionRevision;
    const resolved = await this.resolveEmail(identifier.trim());
    if (!resolved.ok) return resolved;
    const result = await this.request('auth/v1/token?grant_type=password', {
      method: 'POST',
      body: JSON.stringify({ email: resolved.email, password }),
    });
    if (!result.ok) {
      if (result.offline) return result;
      return { ok: false, error: 'Incorrect username or password.' };
    }
    if (!result.data?.access_token) return { ok: false, error: 'Incorrect username or password.' };
    const fallback = result.data.user?.user_metadata?.username || identifier.trim().split('@')[0];
    const profile = await this.ensureProfile(result.data.user, fallback, result.data.access_token);
    if (!profile.ok) {
      return profile;
    }
    if (requestRevision !== this.sessionRevision) {
      return { ok: false, superseded: true, error: 'The active account changed in another tab. Try again if needed.' };
    }
    const session = {
      ...result.data,
      user: {
        ...result.data.user,
        user_metadata: { ...(result.data.user?.user_metadata || {}), username: profile.username },
      },
    };
    const persisted = this.writeSession(session);
    return {
      ok: true,
      username: profile.username,
      persistent: persisted.ok,
      warning: persisted.ok ? null : 'Signed in for this tab, but the browser blocked session storage.',
    };
  }

  async signUp({ username, email, password }) {
    const cleanUsername = typeof username === 'string' ? username.trim() : '';
    const cleanEmail = typeof email === 'string' ? email.trim() : '';
    if (!USERNAME_PATTERN.test(cleanUsername)) {
      return { ok: false, error: 'Username must be 3–20 characters using letters, numbers, or underscore.' };
    }
    if (!cleanEmail.includes('@') || cleanEmail.length > 254) return { ok: false, error: 'Enter a valid email address.' };
    if (typeof password !== 'string' || password.length < 6) {
      return { ok: false, error: 'Password must contain at least 6 characters.' };
    }
    if (password.length > 128) return { ok: false, error: 'Password must contain at most 128 characters.' };
    const requestRevision = this.sessionRevision;
    const result = await this.request('auth/v1/signup', {
      method: 'POST',
      body: JSON.stringify({ email: cleanEmail, password, data: { username: cleanUsername } }),
    });
    if (!result.ok) return result;
    if (!result.data?.access_token) return { ok: true, confirmed: false, username: cleanUsername };
    const profile = await this.ensureProfile(result.data.user, cleanUsername, result.data.access_token);
    if (!profile.ok) {
      return profile;
    }
    if (requestRevision !== this.sessionRevision) {
      return { ok: false, superseded: true, error: 'The active account changed in another tab. Try again if needed.' };
    }
    const session = {
      ...result.data,
      user: {
        ...result.data.user,
        user_metadata: { ...(result.data.user?.user_metadata || {}), username: profile.username },
      },
    };
    const persisted = this.writeSession(session);
    return {
      ok: true,
      confirmed: true,
      username: profile.username,
      persistent: persisted.ok,
      warning: persisted.ok ? null : 'Account created for this tab, but the browser blocked session storage.',
    };
  }

  async signOut() {
    const token = this.session?.access_token;
    // Local sign-out is immediate and invalidates any refresh already in
    // flight. Revoking the server session remains best-effort afterward.
    const persisted = this.writeSession(null);
    if (token) {
      await this.request('auth/v1/logout', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    }
    return {
      ok: true,
      persistent: persisted.ok,
      warning: persisted.ok
        ? null
        : 'Signed out in this tab, but the browser could not remove its stored session.',
    };
  }
}

export function dungeonMarketAuth() {
  return new DungeonMarketAuth({
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  });
}
